import React, { useCallback, useContext, useMemo, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Hr from 'components/Common/Hr'
import Image from 'components/Common/Image'
import MarkdownRender from 'LuxKit/MarkdownRender'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Pane from 'components/Common/Pane'
import TextButton from 'LuxKit/Button/TextButton'
import { getCruiseCountriesByItinerary, getCruisePortsByItinerary, offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import CruiseSupportButton from './CruiseSupportButton'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import CruiseInclusionList from 'components/Cruises/CruiseInclusion/CruiseInclusionList'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ModalContext from 'contexts/ModalContext'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import ContactUSP from 'components/OfferPage/ContactUSP'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import TextLink from 'LuxKit/TextLink'
import Divider from 'LuxKit/Divider'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { trackClientEvent } from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import CruiseLuxPlusLabel from 'components/Cruises/CruiseLuxPlusLabel'

const Root = styled.div`
  display: grid;
  grid-template-areas:
      "top-bar top-bar top-bar"
      ". . ."
      "title title ."
      ". . ."
      "details details details"
      ". . ."
      "promo promo promo"
      ". . ."
      "overview overview overview"
      ". . ."
      "description description description"
      ". . ."
      "journey journey journey"
      ". . ."
      "top-support-banner top-support-banner top-support-banner"
      ". . ."
      "bottom-support-banner bottom-support-banner bottom-support-banner"
      ;
  grid-template-columns: 1fr;
  grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(8)}
      auto
      ${rem(12)}
      auto
      ${rem(12)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto;
  max-width: 100%;


  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "top-bar . overview"
      ". . overview"
      "title . overview"
      ". . overview"
      "details . overview"
      ". . overview"
      "promo . overview"
      ". . overview"
      "description . overview"
      ". . overview"
      "journey . overview"
      ". . overview"
      "top-support-banner . overview"
      "bottom-support-banner . ."; /* to create a fake empty area to push content up */
    grid-template-columns: 1fr ${rem(32)} ${rem(400)};
    grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(12)}
      auto
      ${rem(12)}
      auto
      ${rem(24)}
      auto
      ${rem(16)}
      auto
      ${rem(16)}
      auto
      ${rem(24)}
      auto
      auto;
  }
`

const OverviewArea = styled.div`
  grid-area: overview;
`

const JourneyArea = styled.div`
  grid-area: journey;
`

const ContactUSPArea = styled.div`
  padding: ${rem(20)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  grid-area: top-support-banner;
`

const TopBarArea = styled.div`
  grid-area: top-bar;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const TitleArea = styled.div`
  grid-area: title;
`

const OperatorLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const OperatorLogoContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    align-self: center;
    flex-shrink: 0;
    max-height: ${rem(135)};
    width: ${rem(150)};
  }
`

const DetailItemsContainer = styled.div`
  display: flex;
  grid-area: details;
  flex-direction: column;
  gap: ${rem(2)};

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(16)};
  }
`

const OverviewPane = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(24)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
    border-radius: ${props => props.theme.borderRadius.S};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    box-shadow: ${props => props.theme.shadow.flat.small};
  }
`

const OverviewPaneDetailItemsContainer = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);

  &.three-columns {
    display: flex;
    gap: ${rem(24)};
  }
`

const OverviewPanePriceContainer = styled.div`
  display: flex;
  flex-direction: column;

  > :last-child {
    padding-top: ${rem(8)};
  }
`

const PromotionArea = styled.div`
  grid-area: promo;
`

const ShipDescriptionArea = styled.div`
  grid-area: description;
`

export const TextLinkVendor = styled(TextLink)`
  padding-left: ${rem(4)};
`

interface Props {
  offer: App.CruiseOffer
  showPackageOptions: () => void
}

function CruiseOverview(props: Props) {
  const {
    offer,
    showPackageOptions,
  } = props
  const intl = useIntl()
  const {
    lowestPrice,
    memberLowestPrice,
    canSeeLuxPlusLabel,
    standardInclusions,
    luxPlusInclusions,
  } = useCruiseOfferView({ offer })
  const { departurePort, returnPort, name, ship, itinerary } = offer
  const { lowestOverallPriceDetails } = offer.mainDepartureDetails
  const { discountPills } = lowestOverallPriceDetails
  const shipInfoDescription = ship.shipInfo?.find(info => info.dataTypeName === 'Summary')?.description
  const offerDescription = offer.description || (shipInfoDescription || offer.destinationDescription)

  // operator
  const operatorName = offer.cruiseLine.name
  const operatorLogo = offer.cruiseLine.imageId

  const hasTheSameStartAndEndPort = departurePort === returnPort

  const ports = useMemo(() => getCruisePortsByItinerary(itinerary), [itinerary])
  const countries = useMemo(() => getCruiseCountriesByItinerary(itinerary), [itinerary])

  const returnPortCity = useMemo(() => returnPort?.split(',')?.[0], [returnPort])
  const departurePortCity = useMemo(() => departurePort?.split(',')?.[0], [departurePort])

  const hasValidDepartureDates = useMemo(() => offerHasValidDepartures(offer), [offer])

  const {
    primaryPrice,
    secondaryPrice,
  } = lowestPrice

  const showModal = useContext(ModalContext)
  const openDiscountTsAndCsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const shouldTrackPrice = lowestOverallPriceDetails && hasValidDepartureDates && !!primaryPrice?.total
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: primaryPrice.total.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, primaryPrice?.total, offer?.id])

  return (
    <Root className="OfferIntro">
      <TitleArea className="title-area" data-testid="cruise-offer-overview">
        <Group direction="horizontal" gap={8} horizontalAlign="space-between">
          <Heading as="h1" variant="heading2">
            {name}
          </Heading>
          {operatorLogo && <OperatorLogoContainer>
            <OperatorLogoImage
              id={operatorLogo}
              dpr={2}
              quality="eco"
              width={150}
              fit="center"
              alt={operatorName}
              aria-hidden="true"
            />
          </OperatorLogoContainer>}
        </Group>
      </TitleArea>

      <DetailItemsContainer>
        {!!operatorName && <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="jXo5KEojgJHWwKD9Biho"
            defaultMessage="Cruise line: <b>{operatorName}</b>"
            description="Cruise line label with operator name in the cruise offer or booking section"
            values={{
              operatorName,
              b: (chunks: React.ReactNode) => <b>{chunks}</b>,
            }}
          />
        </BodyText>}

        <Divider direction="vertical" kind="secondary" />

        {!!ship && <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="5GtGndMbb0vOloVTwOJF"
            defaultMessage="Cruise ship: <b>{shipName}</b>"
            description="Cruise ship label with ship name in the cruise offer or booking section"
            values={{
              shipName: ship.name,
              b: (chunks: React.ReactNode) => <b>{chunks}</b>,
            }}
          />
        </BodyText>}
      </DetailItemsContainer>

      <PromotionArea>
        <CruiseInclusionList
          offer={offer}
          evergreenInclusions={offer.evergreenInclusions}
          standardInclusions={standardInclusions}
          luxPlusInclusions={luxPlusInclusions}
          loyaltyInclusionVariant="offer-page"
          gap={16}
        />
      </PromotionArea>

      <ShipDescriptionArea>
        {!!offerDescription && <>
          <CSSBreakpoint max="mobile">
            <LargeContentToggle
              maxHeight={100}
              showMoreText={intl.formatMessage({
                id: 'jvRqMa64z5N1GL8KfM2w',
                defaultMessage: 'Read more',
                description: 'Action button label to read more and expand a truncated content section',
              })}
              showLessText={intl.formatMessage({
                id: 'ZbUsqTgg1rZed9jx6lnc',
                defaultMessage: 'Read less',
                description: 'Action button label to collapse content in the cruises section',
              })}
            >
              <MarkdownRender
                fontSize="medium"
                fontWeight="normal"
                type="compact"
                content={offerDescription}
              />
            </LargeContentToggle>
          </CSSBreakpoint>

          <CSSBreakpoint min="tablet">
            <MarkdownRender
              fontSize="medium"
              fontWeight="normal"
              type="compact"
              content={offerDescription}
            />
          </CSSBreakpoint>
        </>}
      </ShipDescriptionArea>

      <TopBarArea className="top-bar-area">
        <BodyTextBlock variant="medium">
          {hasTheSameStartAndEndPort && <span>
            <FormattedMessage
              id="4hYAGlgNNjRcOyR8ABSu"
              defaultMessage="Starts and ends in <b>{portCity}</b>"
              description="Port information when cruise starts and ends in same city"
              values={{
                portCity: returnPortCity,
                b: (chunks: React.ReactNode) => <b>&nbsp;{chunks}</b>,
              }}
            />
          </span>}
          {!hasTheSameStartAndEndPort && <span>
            <FormattedMessage
              id="dcz1lmt8ZdCHFNGcP6y5"
              defaultMessage="Starts in <b>{departureCity}</b>, ends in <b>{returnCity}</b>"
              description="Port information when cruise starts and ends in different cities"
              values={{
                departureCity: departurePortCity,
                returnCity: returnPortCity,
                b: (chunks: React.ReactNode) => <b>&nbsp;{chunks}</b>,
              }}
            />
          </span>}
        </BodyTextBlock>
      </TopBarArea>

      <OverviewArea>
        <OverviewPane>
          <Heading variant="heading5">
            <FormattedMessage
              id="FFiOyhdqjr-RgDNQNR0B"
              defaultMessage="Overview"
              description="Heading for cruise overview section in the cruise offer or booking section"
            />
          </Heading>
          <OverviewPaneDetailItemsContainer>
            <BodyTextBlock variant="medium" startIcon={<LineClockIcon />}>
              {intl.formatMessage({
                id: 'CLy6gZpbx6rczN1kqb2H',
                defaultMessage: '{count, plural, one {# night} other {# nights}}',
                description: 'Pluralised count of nights for the duration of a cruise itinerary listing',
              }, { count: offer.duration })}
            </BodyTextBlock>
            {!!ports?.length && <BodyTextBlock variant="medium" startIcon={<LineMapIcon />}>
              {intl.formatMessage({
                id: 'uLRiwPOxWXbbQ_N6N4e8',
                defaultMessage: '{count, plural, one {# port} other {# ports}}',
                description: 'Number of ports visited on cruise itinerary in the cruise section',
              }, { count: ports.length })}
            </BodyTextBlock>}
            {!!countries?.length && <BodyTextBlock variant="medium" startIcon={<LineGlobeIcon />}>
              {intl.formatMessage({
                id: 'LQprTJXP8GRDyA1AspwP',
                defaultMessage: '{count, plural, one {# country} other {# countries}}',
                description: 'Number of countries visited on cruise itinerary in the cruises section',
              }, { count: countries.length })}
            </BodyTextBlock>}
          </OverviewPaneDetailItemsContainer>

          <CSSBreakpoint only="mobile">
            <CruiseLoyaltyPoints
              offer={offer}
              calculationType="estimate"
            />
          </CSSBreakpoint>

          <CSSBreakpoint min="tablet">
            <Group direction="vertical" gap={16}>
              <Hr colour="light-grey" />

              <Group direction="vertical" gap={16}>
                <div>
                  {lowestOverallPriceDetails && hasValidDepartureDates && <OverviewPanePriceContainer>
                    <Group direction="vertical" gap={4}>
                      <div>
                        <PriceRowPriceCaption>
                          <FormattedMessage
                            id="ZnyFea8AXQGoEd-b-_JC"
                            defaultMessage="From"
                            description="Price label prefix indicating starting price in the price breakdown"
                          />
                        </PriceRowPriceCaption>
                        <LuxPlusPriceStack
                          size="L"
                          price={primaryPrice.total}
                          memberPrice={memberLowestPrice?.primaryPrice.total}
                          saleUnit={primaryPrice.type}
                        />

                      </div>
                      {secondaryPrice && <PriceRowCaption>
                        <FormattedMessage
                          id="GS-URylh1YojT846ev_Q"
                          defaultMessage="{price}/{priceType} · Twin Share"
                          description="Secondary price display with twin share indicator in the cruises section"
                          values={{
                            price: <FormatCurrency value={secondaryPrice.total} />,
                            priceType: secondaryPrice.type,
                          }}
                        />
                      </PriceRowCaption>}

                      {canSeeLuxPlusLabel && <CruiseLuxPlusLabel
                        isModalEnabled
                        offer={offer}
                      />}
                    </Group>
                    {discountPills && <PriceRowValueDiscountWithCaption
                      size="L"
                      originalValue={discountPills.price}
                      discountPercentage={discountPills.discountPercentage}
                      onInfoIconClick={openDiscountTsAndCsModal}
                    />}
                  </OverviewPanePriceContainer>}
                </div>

                <AgentCommissionPanel
                  offerId={offer.id}
                  offer={offer}
                  offerTotal={primaryPrice.total ?? 0}
                  retailValue={discountPills ? discountPills.price : 0}
                />

                <CruiseLoyaltyPoints
                  offer={offer}
                  calculationType="estimate"
                />
              </Group>

              <Group direction="vertical" gap={12}>
                {hasValidDepartureDates && <TextButton
                  kind="primary"
                  onClick={showPackageOptions}
                  fit="full-width"
                >
                  <FormattedMessage
                    id="jz0dx7BQ7GSRVwQkkFjH"
                    defaultMessage="Check availability"
                    description="Button text to check cruise availability in the cruise section"
                  />
                </TextButton>}

                {!hasValidDepartureDates && <Group direction="vertical" gap={12}>
                  <BodyText variant="large" weight="bold" align="center">
                    <FormattedMessage
                      id="Qfe77Kry9p_oD3uY5Uwi"
                      defaultMessage="This offer has sold out"
                      description="Message when cruise offer is sold out in the cruise offer or booking section"
                    />
                  </BodyText>
                  <TextButton kind="secondary" to="/cruises">
                    <FormattedMessage
                      id="gdHYkGeOKO7bRK3FYwdz"
                      defaultMessage="Browse all cruise offers"
                      description="Link text to browse all cruise offers in the cruise offer or booking section"
                    />
                  </TextButton>
                </Group>}

                <CruiseSupportButton />
              </Group>
            </Group>
          </CSSBreakpoint>
        </OverviewPane>
      </OverviewArea>

      <JourneyArea>
        <BodyText variant="medium" weight="normal">
          <FormattedMessage
            id="0fzM8feaDW9phy6hxsqT"
            defaultMessage="Your journey takes you to: <bold>{ports}</bold>"
            description="Text showing cruise journey ports in the cruise offer or booking section"
            values={{
              ports: ports.join(' - '),
              bold: (chunks: React.ReactNode) => (
                <BodyText variant="medium" weight="bold" as="span">
                  {chunks}
                </BodyText>
              ),
            }}
          />
        </BodyText>
      </JourneyArea>

      <ContactUSPArea>
        <ContactUSP />
      </ContactUSPArea>
    </Root>
  )
}

export default CruiseOverview
